type LeafValues<T> = T extends string ? T : { [K in keyof T]: LeafValues<T[K]> }[keyof T];

/**
 * Strictly typed route path constants — the single source of truth for route path segments.
 * Used by route definitions (`defineRoute`), guards, NavigationService, and components.
 *
 * Follows the project's const-map-over-enum convention. Feature routes are grouped;
 * `Legacy` holds old segments kept only for backwards-compatible redirect rules.
 */
export const AppPathsMap = {
  Root: '',
  Homepage: '/',
  Home: 'home',
  Dashboard: 'dashboard',
  Logout: 'logout',
  Login: 'login',
  Profile: 'profile',
  DocumentLibrary: 'document-library',
  TransparencyRegister: 'transparency-register',
  Contact: 'contact',
  ContractExpired: 'contract-expired',
  IconExample: 'icon-example',

  Kyc: { Root: 'kyc-check', Overview: 'overview', Create: 'create', Dashboard: 'dashboard', Review: 'review' },
  LocationAnalysis: { Root: 'location-analysis', Introduction: 'introduction', Create: 'create' },
  RiskAnalysis: {
    Root: 'risk-analysis',
    Introduction: 'introduction',
    Questionnaire: 'risk-questionnaire',
    // The document centre: the organization's own process documents, which the document library does not list.
    Documents: 'documents',
  },
  Academy: { Root: 'training', Dashboard: 'dashboard', Overview: 'overview', Users: 'users' },
  Faq: {
    Root: 'faq',
    KycUserCreation: 'kyc-user-creation',
    RiskAnalysis: 'risk-analysis',
    Academy: 'academy',
    AmlDesk: 'aml-desk',
    MoneyLaundering: 'money-laundering',
  },
  Error: {
    Unauthorized: 'unauthorized',
    NoGrants: 'no-grants',
    GrantLoadError: 'grant-load-error',
    LoginError: 'login-error',
    NotFound: 'not-found',
    Maintenance: 'maintenance',
    BackendUnavailable: 'backend-unavailable',
    UserNotActive: 'account-inactive',
    SessionUnavailable: 'session-unavailable',
  },
  // Old routes previously emailed to customers; kept only for redirect rules in route files.
  Legacy: {
    Kyc: {
      Root: 'kyc',
      Introduction: 'kyc-introduction',
      Create: 'kyc-create',
      Overview: 'kyc-overview',
      CheckReview: 'check-review',
    },
    LocationAnalysis: { Introduction: 'la-introduction', Create: 'la-create' },
  },
} as const;

export type AppPath = LeafValues<typeof AppPathsMap>;
